import { applyEnrichment, enrichmentFor, readBrowserEnvironment, shouldDrop } from "./envelope-rules.js";
import { logging } from "./logging.js";
import { buildSnippet, isTelemetryDisabled } from "./sdk-config.js";
import { createDropReporter, failClosed, sanitizeOrDrop } from "./telemetry-drops.js";
import { ClickAnalyticsPlugin } from "@microsoft/applicationinsights-clickanalytics-js";
import { ApplicationInsights } from "@microsoft/applicationinsights-web";

//#region src/web/provider.ts
const CLICK_ANALYTICS_CONFIG = { autoCapture: true };
const clickPluginInstance = new ClickAnalyticsPlugin();
const ignore = () => {};
const DISABLED_INSTANCE = {
	logEvent: ignore,
	logError: ignore,
	logMetric: ignore,
	logUnknownError: ignore,
	getSessionDetails: () => ({ sessionId: "" }),
	setUserId: ignore,
	addPlugin: ignore,
	updateConfig: ignore
};
/**
* Creates the page's single telemetry provider on first use. Every later
* call returns that same instance and ignores its options.
*/
const TelemetryProviderFactory = (() => {
	let instance;
	return { getInstance: (options) => {
		instance ??= createInstance(options);
		return instance;
	} };
})();
/**
* Starts the page's telemetry provider with click analytics auto-capture
* enabled. Only the first call creates it; later calls return that instance.
*
* @param instrumentationKey The App Insights connection string.
* @param buildNumber Tags every envelope with the app build, when given.
* @param cloudRole The `ai.cloud.role` every envelope is tagged with.
*/
const initializeProvider = (instrumentationKey, buildNumber = "", cloudRole) => TelemetryProviderFactory.getInstance({
	instrumentationKey,
	buildNumber,
	cloudRole,
	extensions: [clickPluginInstance],
	extensionConfig: { [clickPluginInstance.identifier]: CLICK_ANALYTICS_CONFIG }
});
function createInstance(options) {
	if (isTelemetryDisabled(options.instrumentationKey)) return DISABLED_INSTANCE;
	const appInsights = new ApplicationInsights(buildSnippet(options));
	appInsights.addTelemetryInitializer(createInitializer(appInsights, options));
	startTracking(appInsights, options.buildNumber);
	return {
		...loggingApi(appInsights),
		...sdkApi(appInsights)
	};
}
function createInitializer(appInsights, { cloudRole, buildNumber }) {
	const drops = createDropReporter((name, count, properties) => appInsights.trackMetric({
		name,
		average: count
	}, properties));
	return failClosed(drops.record, (envelope) => processEnvelope(envelope, {
		cloudRole,
		buildNumber
	}, drops));
}
function processEnvelope(envelope, context, drops) {
	if (shouldDrop(envelope)) return false;
	applyEnrichment(envelope, enrichmentFor(envelope, context, readBrowserEnvironment()));
	return drops.isSending() || sanitizeOrDrop(envelope, drops.record);
}
function startTracking(appInsights, buildNumber) {
	appInsights.loadAppInsights();
	appInsights.trackPageView();
	if (buildNumber) {
		appInsights.context.application.build = buildNumber;
		appInsights.context.application.ver = buildNumber;
	}
}
function loggingApi(appInsights) {
	return {
		logEvent: (event) => logging.logEvent(event, appInsights),
		logError: (event) => logging.logError(event, appInsights),
		logMetric: (event) => logging.logMetric(event, appInsights),
		logUnknownError: (event, error) => logging.logUnknownError(event, error, appInsights)
	};
}
function sdkApi(appInsights) {
	return {
		getSessionDetails: () => ({
			sessionId: appInsights.context.getSessionId(),
			traceId: appInsights.context.telemetryTrace.traceID
		}),
		setUserId: (userId) => appInsights.setAuthenticatedUserContext(userId),
		addPlugin: ((plugin, ...rest) => appInsights.addPlugin(plugin, ...rest)),
		updateConfig: (config, mergeExisting) => appInsights.updateCfg(config, mergeExisting)
	};
}

//#endregion
export { initializeProvider };